import { Circle, Fill, Icon, Style } from 'ol/style';

const preloadedImages = {};

// Function to preload an image and return a promise
const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        if (preloadedImages[src]) {
            resolve(preloadedImages[src]);
            return;
        }

        const img = new Image();

        img.onload = () => {
            resolve(img);
        };

        img.onerror = (err) => {
            reject(new Error(`Failed to load ${src}`));
        };

        img.src = src;
    });
};
export const preloadImages = async () => {
    try {
        const imagePaths = {
            redLeafIcon: '/images/icons/leaf-red.png',
            yellowLeafIcon: '/images/icons/1.png',
            blueLeafIcon: '/images/icons/2.png',
            orangeLeafIcon: '/images/icons/3.png',
            turquoiseLeafIcon: '/images/icons/4.png',
            purpleLeafIcon: '/images/icons/5.png',
            pinkLeafIcon: '/images/icons/leaf-pink.png',
            brownLeafIcon: '/images/icons/leaf-brown.png',
            flag: '/images/icons/flag.png',
            flagReview: '/images/icons/flag2.png',
            imgGrey: '/images/icons/6.png',
            imgTechoneIcon: '/images/icons/t1-6.png',
            imgTick: '/images/icons/tick.png',
            imgRecent: '/images/icons/geo_marker.png',
            whiteLeaf: '/images/icons/leaf-1-new.png',
            wsOrange: '/images/icons/leaf-2-new.png',
            wsRed: '/images/icons/leaf-3-new.png',
            stumpIcon: '/images/icons/stump_icon.png',
            techOneActioned: '/images/icons/t1-6.png',
            waterComplete: '/images/icons/water-c.png',
            waterComplete1: '/images/icons/water-c-1.png',
            waterComplete2: '/images/icons/water-c-2.png',
            waterIncomplete: '/images/icons/water-4.png',
            waterIncomplete1: '/images/icons/water-1.png',
            waterIncomplete2: '/images/icons/water-2.png',
            private1: '/images/icons/private_1.png',
            private2: '/images/icons/private_2.png',
            private4: '/images/icons/private_4.png',
            grouped1: '/images/icons/grouped_1.png'

        };

        const priorityPaths = {
            t1Icons: ['/images/icons/t1-1.png', '/images/icons/t1-2.png', '/images/icons/t1-3.png'],
            stormIcons: ['/images/icons/storm-1.png', '/images/icons/storm-2.png', '/images/icons/storm-3.png'],
            leafIcons: ['/images/icons/leaf-1.png', '/images/icons/leaf-2.png', '/images/icons/leaf-3.png'],
            powerIcons: ['/images/icons/power_1.png', '/images/icons/power_2.png']
        };

        const allImagePromises = [];

        Object.entries(imagePaths).forEach(([key, src]) => {
            allImagePromises.push(
                preloadImage(src)
                    .then((img) => {
                        preloadedImages[key] = img;
                        return { key, success: true };
                    })
                    .catch((error) => {
                        console.error(`Failed to preload ${key}:`, error);
                        return { key, success: false };
                    })
            );
        });

        // Add priority images
        Object.entries(priorityPaths).forEach(([key, paths]) => {
            paths.forEach((src, index) => {
                const imageKey = `${key}-${index + 1}`;
                allImagePromises.push(
                    preloadImage(src)
                        .then((img) => {
                            preloadedImages[imageKey] = img;
                            return { key: imageKey, success: true };
                        })
                        .catch((error) => {
                            console.error(`Failed to preload ${imageKey}:`, error);
                            return { key: imageKey, success: false };
                        })
                );
            });
        });

        // Wait for ALL images to complete (either success or failure)
        const results = await Promise.allSettled(allImagePromises);

        const successfulLoads = results.filter(result =>
            result.status === 'fulfilled' && result.value.success
        );

        const failedLoads = results.filter(result =>
            result.status === 'rejected' ||
            (result.status === 'fulfilled' && !result.value.success)
        );


        if (failedLoads.length > 0) {
            console.warn('Failed images:', failedLoads.map(f =>
                f.status === 'fulfilled' ? f.value.key : 'unknown'
            ));
        }

        // Return whether all critical images loaded (you can define which are critical)
        // const criticalImages = ['orangeLeafIcon', 'flagIcon', 'flagReviewIcon'];
        // const criticalSuccess = criticalImages.every(key => preloadedImages[key]);
        //
        // if (!criticalSuccess) {
        //     console.error('Critical images failed to load!');
        //     return false;
        // }

        return true;
    } catch (e) {
        console.error('Error in preloadImages:', error);
        return false;
    }
};

const defaultAnchorAttributes = {
    anchor: [0.5, 0.5],
    anchorXUnits: 'fraction',
    anchorYUnits: 'fraction'
};
export const ForestreeIconFactory = (addtAttr = {}) => {
    return {
        greenTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages[`leafIcons-1`],
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        redTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.redLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        blueTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.blueLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        orangeTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.orangeLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        yellowTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.yellowLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        turquoiseTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.turquoiseLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        purpleTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.purpleLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        pinkTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.pinkLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        brownTreeIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.brownLeafIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),

        greyTreeIcon: new Icon({
            scale: 0.4,
            img: preloadedImages.imgGrey,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        flagIcon: new Icon({
            scale: 0.4,
            anchor: [0.5, 0.5],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            img: preloadedImages.flag,
            imgSize: [64, 64],
            opacity: 1,
            ...addtAttr
        }),
        flagSmallIcon: new Icon({
            scale: 0.3,
            anchor: [-0.1, 1.0],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            img: preloadedImages.flag,
            imgSize: [22, 22],
            opacity: 1,
            ...addtAttr
        }),
        flagReviewIcon: new Icon({
            scale: 0.4,
            anchor: [0.5, 0.5],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            img: preloadedImages.flagReview,
            imgSize: [64, 64],
            opacity: 1,
            ...addtAttr
        }),
        flagReviewSmallIcon: new Icon({
            scale: 0.3,
            anchor: [-0.1, 1.0],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            img: preloadedImages.flagReview,
            imgSize: [22, 22],
            opacity: 1,
            ...addtAttr
        }),
        recentTreeIcon: new Icon({
            scale: 0.6,
            anchor: [-0.2, 1.2],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            opacity: 0.9,
            img: preloadedImages.imgRecent,
            imgSize: [22, 22]
        }),
        tickCompleteIcon: new Icon({
            scale: 0.4,
            img: preloadedImages.imgTick,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        techOneIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.imgTechoneIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        techOnePriorityIcon: (priority) =>
            new Icon({
                scale: 0.6,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                img: preloadedImages[`t1Icons-${priority}`],
                imgSize: [48, 48],
                ...addtAttr
            }),
        techOneActionedIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.techOneActioned,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),

        stormPriorityIcon: (priority) =>
            new Icon({
                scale: 0.6,
                img: preloadedImages[`stormIcons-${priority}`],
                imgSize: [48, 48],
                ...defaultAnchorAttributes,
                ...addtAttr
            }),
        leafWorksPriorityIcon: (priority) => {
            return new Icon({
                scale: 0.5,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                img: preloadedImages[`leafIcons-${priority}`],
                imgSize: [48, 48],
                ...addtAttr
            });
        },
        selectTreeCircleStyle: new Style({
            image: new Circle({
                radius: 16,
                fill: new Fill({
                    color: 'rgba(0, 0, 255, 0.5)'
                })
            })
        }),
        wsOrange: new Icon({
            scale: 0.5,
            img: preloadedImages.wsOrange,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        wsRed: new Icon({
            scale: 0.5,
            img: preloadedImages.wsRed,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        stumpIcon: new Icon({
            scale: 0.8,
            img: preloadedImages.stumpIcon,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        powerlineIdIcon: (id) =>
            new Icon({
                scale: 0.8,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                img: preloadedImages[`powerIcons-${id}`],
                imgSize: [48, 48],
                ...addtAttr
            }),
        waterComplete: new Icon({
            scale: 0.5,
            img: preloadedImages.waterComplete,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        waterComplete1: new Icon({
            scale: 0.5,
            img: preloadedImages.waterComplete1,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        waterComplete2: new Icon({
            scale: 0.5,
            img: preloadedImages.waterComplete2,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        waterIncomplete: new Icon({
            scale: 0.5,
            img: preloadedImages.waterIncomplete,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        waterIncomplete1: new Icon({
            scale: 0.5,
            img: preloadedImages.waterIncomplete1,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        waterIncomplete2: new Icon({
            scale: 0.5,
            img: preloadedImages.waterIncomplete2,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        }),
        whiteLeafIcon: new Icon({
            scale: 0.5,
            img: preloadedImages.whiteLeaf,
            imgSize: [48, 48],
            ...defaultAnchorAttributes,
            ...addtAttr
        })

    };
};
